import { type DropdownMenuOption } from '@air/primitive-dropdown-menu';

import { type SelectionBarActions } from '~/components/v2/components/shared/selectionBar/selectionBarActions';
import { getDropdownMenuOption } from '~/utils/menuOptions/getDropdownMenuOption';

const CUSTOM_FIELDS_OPTION_IDS = new Set(['bulk-edit-custom-field', 'bulk-edit-custom-fields', 'edit-custom-fields']);

const COMPACT_PREFIX_SEPARATOR: DropdownMenuOption = {
  id: 'compact-prefix-separator',
  type: 'separator',
};

const hasMenuOptionId = (options: DropdownMenuOption[], id: string) => options.some((option) => option.id === id);

const stripLeadingSeparators = (menuOptions: DropdownMenuOption[]): DropdownMenuOption[] => {
  const firstItemIndex = menuOptions.findIndex((option) => option.type !== 'separator');

  if (firstItemIndex <= 0) {
    return menuOptions;
  }

  return menuOptions.slice(firstItemIndex);
};

const insertBeforeCustomFields = (options: DropdownMenuOption[], toInsert: DropdownMenuOption[]) => {
  if (toInsert.length === 0) {
    return options;
  }

  const customFieldsIndex = options.findIndex((option) => CUSTOM_FIELDS_OPTION_IDS.has(option.id));
  const insertIndex = customFieldsIndex === -1 ? options.length : customFieldsIndex;
  const nextOptions = [...options];
  nextOptions.splice(insertIndex, 0, ...toInsert);
  return nextOptions;
};

const injectWithTrailingSeparator = (options: DropdownMenuOption[], injected: DropdownMenuOption[]) => {
  const deduped = injected.filter((option) => !hasMenuOptionId(options, option.id));

  if (deduped.length === 0) {
    return stripLeadingSeparators(options);
  }

  if (options.length === 0 || !deduped.some((option) => option.type !== 'separator')) {
    return stripLeadingSeparators([...deduped, ...options]);
  }

  if (options[0]?.type === 'separator') {
    return stripLeadingSeparators([...deduped, ...options]);
  }

  return stripLeadingSeparators([...deduped, COMPACT_PREFIX_SEPARATOR, ...options]);
};

export type ApplySelectionBarOverflowTuckParams = {
  options: DropdownMenuOption[];
  actions: SelectionBarActions;
  editTagsOption: DropdownMenuOption | null;
  favoriteOption: DropdownMenuOption | null;
  customFieldsSubmenu: DropdownMenuOption | null;
  downloadAction: (() => void) | null;
  shareAction: (() => void) | null;
  isSplitView: boolean;
  hidePrefixActions: boolean;
  agentSessionsEnabled: boolean;
  selectedCount: number;
};

export type ApplySelectionBarOverflowTuckResult = {
  options: DropdownMenuOption[];
  actions: SelectionBarActions;
};

/**
 * Unifies split-view tuck, single-asset agent-panel edit-tags injection, and compact-width prefix tuck.
 * Favorite always lives in the ellipsis menu, never as a prefix icon.
 */
export const applySelectionBarOverflowTuck = ({
  options,
  actions,
  editTagsOption,
  favoriteOption,
  customFieldsSubmenu,
  downloadAction,
  shareAction,
  isSplitView,
  hidePrefixActions,
  agentSessionsEnabled,
  selectedCount,
}: ApplySelectionBarOverflowTuckParams): ApplySelectionBarOverflowTuckResult => {
  let nextOptions = options;
  const nextActions = { ...actions };

  if (
    agentSessionsEnabled &&
    !isSplitView &&
    !hidePrefixActions &&
    selectedCount === 1 &&
    editTagsOption &&
    !hasMenuOptionId(nextOptions, 'edit-tags')
  ) {
    nextOptions = insertBeforeCustomFields(nextOptions, [editTagsOption]);
  }

  if (isSplitView) {
    const downloadOption: DropdownMenuOption | null = downloadAction
      ? {
          ...getDropdownMenuOption('download'),
          onSelect: downloadAction,
          type: 'item',
        }
      : null;
    const shareOption: DropdownMenuOption | null = shareAction
      ? {
          ...getDropdownMenuOption('share'),
          onSelect: shareAction,
          type: 'item',
        }
      : null;

    const optionsToInsert: DropdownMenuOption[] = [];

    if (editTagsOption) {
      optionsToInsert.push(editTagsOption);
      nextActions.editTags = undefined;
    }

    if (favoriteOption) {
      optionsToInsert.push(favoriteOption);
      nextActions.favorite = undefined;
    }

    const removeSeparatorIndex = nextOptions.findIndex((option) => option.id === 'delete-separator');
    const customFieldsIndex = nextOptions.findIndex((option) => CUSTOM_FIELDS_OPTION_IDS.has(option.id));
    const baseInsertIndex = customFieldsIndex === -1 ? nextOptions.length : customFieldsIndex + 1;
    const insertIndex = removeSeparatorIndex === -1 ? baseInsertIndex : Math.min(baseInsertIndex, removeSeparatorIndex);
    const splitOptions = [...nextOptions];

    if (optionsToInsert.length > 0) {
      splitOptions.splice(insertIndex, 0, ...optionsToInsert);
    }

    if (downloadOption && !hasMenuOptionId(splitOptions, downloadOption.id)) {
      const downloadInsertIndex = insertIndex + optionsToInsert.length;
      splitOptions.splice(downloadInsertIndex, 0, downloadOption);
    }

    if (shareOption && !hasMenuOptionId(splitOptions, shareOption.id)) {
      const shareInsertIndex = downloadOption
        ? insertIndex + optionsToInsert.length + 1
        : insertIndex + optionsToInsert.length;
      splitOptions.splice(shareInsertIndex, 0, shareOption);
    }

    nextOptions = splitOptions;
    nextActions.download = undefined;
    nextActions.share = undefined;
    nextActions.addToBoard = undefined;
    nextActions.customFields = undefined;
    nextActions.editInCanvas = undefined;

    return { options: nextOptions, actions: nextActions };
  }

  if (hidePrefixActions) {
    const injected: DropdownMenuOption[] = [];

    if (editTagsOption && nextActions.editTags) {
      injected.push(editTagsOption);
    }

    if (favoriteOption && nextActions.favorite) {
      injected.push(favoriteOption);
    }

    if (customFieldsSubmenu && nextActions.customFields) {
      injected.push(customFieldsSubmenu);
    }

    if (injected.length > 0) {
      nextOptions = injectWithTrailingSeparator(nextOptions, injected);
    } else {
      nextOptions = stripLeadingSeparators(nextOptions);
    }

    nextActions.editTags = undefined;
    nextActions.favorite = undefined;
    nextActions.addToBoard = undefined;
    nextActions.customFields = undefined;
    nextActions.editInCanvas = undefined;
  } else if (favoriteOption && nextActions.favorite) {
    nextOptions = injectWithTrailingSeparator(nextOptions, [favoriteOption]);
    nextActions.favorite = undefined;
  }

  return { options: nextOptions, actions: nextActions };
};

export type ApplySelectAllCompactTuckParams = {
  menuOptions: DropdownMenuOption[];
  actions: SelectionBarActions;
  editTagsOption: DropdownMenuOption | null;
  addToBoardOption: DropdownMenuOption | null;
  customFieldsSubmenu: DropdownMenuOption | null;
};

export const applySelectAllCompactTuck = ({
  menuOptions,
  actions,
  editTagsOption,
  addToBoardOption,
  customFieldsSubmenu,
}: ApplySelectAllCompactTuckParams): ApplySelectionBarOverflowTuckResult => {
  const injected: DropdownMenuOption[] = [];

  if (addToBoardOption && actions.addToBoard) {
    injected.push(addToBoardOption);
  }

  if (editTagsOption && actions.editTags) {
    injected.push(editTagsOption);
  }

  if (customFieldsSubmenu && actions.customFields) {
    injected.push(customFieldsSubmenu);
  }

  return {
    options: injectWithTrailingSeparator(menuOptions, injected),
    actions: {
      ...actions,
      addToBoard: undefined,
      editTags: undefined,
      customFields: undefined,
    },
  };
};
